// Customer Switch (Manage tab) — the PURE grouping rule.
//
// This module exists outside the components ON PURPOSE. It mirrors a BACKEND SQL
// PREDICATE, and a mirror that cannot be asserted in isolation is a mirror that
// drifts. Every rule below has a named counterpart in
// yuso-portals-backend/src/functions/customerSwitch.ts; the tests in
// tests/utils/switchGroup.test.ts pin them.
//
// Nothing here imports a component at runtime (the CustomerSwitchLeg import is
// type-only, and `customerSwitchPayloads` is itself component-free), so the whole
// module loads in a plain node test environment.

import type { CustomerSwitchLeg } from "./CustomerSwitchModal";
import type {
  CompanyServices,
  FlexService,
  InjectionService,
  OfftakeService,
} from "../../../types/portfolioTypes";
import type { ExistingCompany } from "../../../types/addServiceTypes";
import { countForeignActiveOwners } from "../../utils/validation";
/**
 * The single direction→standalone-service-config lookup for the whole Customer Switch
 * feature. Pure, no component edge (customerSwitchPayloads imports CustomerSwitchModal
 * TYPE-ONLY), so this module still loads in a plain node test environment.
 */
import { findDirectionServiceConfig } from "./customerSwitchPayloads";

export type SwitchDirection = "injection" | "offtake";

/**
 * The commodity statuses that make a point handed-over-able. Mirrors
 * yuso-portals-backend LIVENESS.SWITCHABLE (`contractLiveness.ts`) and the same
 * literal set `validation.ts` keys the Customer Switch decision on. NOT the same
 * as End-of-Contract's eligibility, which is "active" alone.
 */
const SWITCHABLE_STATUSES = ["active", "future"];

/**
 * 🔴 LOCKSTEP MIRROR of yuso-portals-backend `src/functions/customerSwitch.ts:39`
 * `FLEX_PRODUCT_IDS`. That array decides which flex contracts the handover
 * actually moves (`getOldCompanyFlexContracts`, `sf.product_id IN (...)`); this
 * one decides what the UI shows as "moves together". If the two disagree the
 * user confirms one set and a different set transfers. Change them together.
 *
 * 🔴 SECOND, NON-OBVIOUS INVARIANT: FLEX_PRODUCT_IDS must stay a SUBSET of the
 * backend's `PRODUCT_ID_MAP` (`src/functions/flexServices.ts`), which is
 * {1,2,3,4,9,11,12}. `/allservices` buckets flex by `asset_type` and returns ONLY
 * the "BESS" / "e-boiler" / "PV curtailment" buckets — a row whose asset_type
 * resolves to "Other" is dropped from the response ENTIRELY. So an id added here
 * (and to the backend list) but not to PRODUCT_ID_MAP describes flex this
 * frontend cannot see at all, invisible to every test below.
 *
 * Note the converse is deliberately NOT symmetrical: product 11 IS in
 * PRODUCT_ID_MAP (as PV curtailment) but is NOT in FLEX_PRODUCT_IDS, so an
 * 11 asset is visible in the portfolio and is NOT handed over. That is the
 * backend's rule, mirrored here on purpose — it is why this module filters on
 * `product_id` and NEVER on the asset-type bucket. Tracked as its own ticket;
 * do not "fix" it here, because that would change what physically transfers.
 */
export const FLEX_PRODUCT_IDS: readonly number[] = [1, 2, 3, 4, 9, 12];

/**
 * The EAN in its STORED form — `sdps_*.ean`, i.e. WITH the "EAN" prefix
 * ("EAN541448860019552976").
 *
 * 🔴 Do NOT reach for `normalizeEan` from `src/api/endOfContract.ts`: that one
 * STRIPS the prefix because the End-of-Contract endpoint wants bare digits, and
 * it is imported by EndContractManager — the adjacent screen. Every backend
 * predicate on this path matches `WHERE s.ean = ?` against the stored string and
 * prefixes nothing, so the prefixed form is the only correct identity here.
 */
export const toStoredEan = (ean: string): string => {
  const trimmed = (ean ?? "").trim();
  if (trimmed === "") return "";
  return /^EAN/i.test(trimmed) ? `EAN${trimmed.slice(3)}` : `EAN${trimmed}`;
};

/** One connection point of company A, both directions fused into a single row. */
export interface CommodityPoint {
  /** Stored (prefixed) EAN — doubles as the selection key. */
  key: string;
  ean: string;
  /** The directions A actually holds on this EAN. */
  directions: SwitchDirection[];
  /** installation_id per direction, used for the site-leg walk. */
  installationIds: Partial<Record<SwitchDirection, string>>;
  /**
   * The STORED `sdps_*.meterType`, PER DIRECTION — kept split because a fused point
   * is built from two rows written at different times by different classifiers, and
   * they genuinely disagree in production. Only the Walloon branch of the meter gate
   * reads this; see `isMeterTypeAcceptable`.
   */
  meterTypes: Partial<Record<SwitchDirection, string>>;
  /**
   * The STORED `sdps_*.dgo` NAME (e.g. "ORES Namur", "Fluvius Antwerpen"). The
   * grid operator is a property of the EAN, so both fused rows carry the same
   * value; the first non-blank one wins, exactly as the site linkage does.
   */
  dgo: string | null;
  siteId: number | null;
  siteName: string | null;
  address: string;
}

/** One flex contract that the handover would carry with the EAN. */
export interface FlexAsset {
  /**
   * `flex-${contract_flex_id}` — unique per flex CONTRACT. Never key on the flex
   * row's `id`: that is the sites-table id, shared by every flex service on the
   * same site (a BESS and a PV on one site would collide).
   */
  key: string;
  contractFlexId: number;
  productId: number;
  name: string;
  address: string;
  /** The flex row IS a sites-table row, so its `id` is the site id. */
  siteId: number;
  /** The SITE's legs (from `sites`), not the flex SDP's own installation. */
  injectionInstallationId: string | null;
  offtakeInstallationId: string | null;
}

const addressOfCommodity = (s: InjectionService | OfftakeService): string =>
  `${s.addressStreet} ${s.addressNumber}, ${s.addressZip} ${s.addressCity}`;

const addressOfFlex = (s: FlexService): string =>
  `${s.address_street} ${s.address_number}, ${s.address_zip} ${s.address_city}`;

/**
 * A's connection points, one row per EAN with both directions fused.
 *
 * The fusion IS source (1) of the group: when A holds injection AND offtake on
 * the same EAN, `resolveSwitchLegs` already emits ONE `combined` leg and the
 * handover happens in a single backend transaction. Splitting it into two rows
 * would be the FT-3581-class trap — leg 1 ends BOTH of A's directions, so leg 2
 * would find zero foreign rows and silently degrade to a normal onboarding.
 */
export const buildCommodityPoints = (
  portfolio: CompanyServices | null | undefined
): CommodityPoint[] => {
  const byEan = new Map<string, CommodityPoint>();

  const add = (
    s: InjectionService | OfftakeService,
    direction: SwitchDirection
  ): void => {
    if (!SWITCHABLE_STATUSES.includes(s.status)) return;
    const key = toStoredEan(s.ean);
    if (key === "") return;
    const existing = byEan.get(key);
    if (existing) {
      if (!existing.directions.includes(direction)) existing.directions.push(direction);
      if (!existing.installationIds[direction]) {
        existing.installationIds[direction] = s.installation_id;
      }
      // PER DIRECTION, never merged: the two rows are written by different
      // classifiers and disagree in production. `isMeterTypeAcceptable` is the
      // only thing allowed to decide what a disagreement means.
      if (existing.meterTypes[direction] === undefined) {
        existing.meterTypes[direction] = s.meterType;
      }
      // Either source row may be the one carrying the site linkage / the dgo.
      if (existing.siteId == null) existing.siteId = s.site_id;
      if (existing.siteName == null) existing.siteName = s.site_name;
      if ((existing.dgo ?? "") === "") existing.dgo = s.dgo ?? null;
      return;
    }
    byEan.set(key, {
      key,
      ean: key,
      directions: [direction],
      installationIds: { [direction]: s.installation_id },
      meterTypes: { [direction]: s.meterType },
      dgo: s.dgo ?? null,
      siteId: s.site_id,
      siteName: s.site_name,
      address: addressOfCommodity(s),
    });
  };

  (portfolio?.services.injection ?? []).forEach((s) => add(s, "injection"));
  (portfolio?.services.offtake ?? []).forEach((s) => add(s, "offtake"));
  return [...byEan.values()];
};

/**
 * The flex contracts the handover would actually move — a faithful mirror of
 * `getOldCompanyFlexContracts` (yuso-portals-backend customerSwitch.ts), clause
 * by clause:
 *
 *   s.company_id = ?                 → implicit: /allservices is already scoped
 *                                      to company A (`WHERE s.company_id = ?`).
 *   INNER JOIN contracts_flex        → `contract_flex_id !== null` (the portfolio
 *                                      uses a LEFT JOIN, so contract-less flex
 *                                      SDPs arrive with a null id).
 *   sf.product_id IN (...)           → FLEX_PRODUCT_IDS, on `product_id`.
 *   cf.end_date IS NULL              → `end_date === null`, STRICTLY.
 *   cf.id = MAX(cf2.id) per sdp_id   → ALREADY APPLIED server-side by
 *                                      /allservices, which joins the flex
 *                                      contract via
 *                                      `ORDER BY cf2.id DESC LIMIT 1`. Do not
 *                                      re-derive it; the response never contains
 *                                      a non-latest contract to filter out.
 *
 * 🔴 The `end_date` clause is THE ONE handover condition /allservices does not
 * apply itself, so it is the one this function genuinely has to reproduce — and
 * it must be STRICT `=== null`. End-of-Contract's equivalent guard treats the
 * MySQL zero-date as "no end date" (`end_date && end_date !== "0000-00-00"`),
 * which INCLUDES a '0000-00-00' row; `IS NULL` EXCLUDES it. Copying EOC's form
 * here would group flex the backend will not move.
 *
 * All three portfolio buckets are unioned: the whitelist spans BESS (1,2,3),
 * e-boiler (4) and PV curtailment (9,12), so reading fewer than three silently
 * narrows it.
 */
export const buildTransferableFlex = (
  portfolio: CompanyServices | null | undefined
): FlexAsset[] => {
  const out: FlexAsset[] = [];
  const add = (s: FlexService): void => {
    if (s.contract_flex_id == null) return;
    // STRICT: '0000-00-00' is NOT null and the backend's `IS NULL` excludes it.
    if (s.end_date !== null) return;
    const productId = s.product_id;
    if (productId == null || !FLEX_PRODUCT_IDS.includes(productId)) return;
    out.push({
      key: `flex-${s.contract_flex_id}`,
      contractFlexId: s.contract_flex_id,
      productId,
      name: s.name,
      address: addressOfFlex(s),
      siteId: s.id,
      injectionInstallationId: s.injection_installation_id,
      offtakeInstallationId: s.offtake_installation_id,
    });
  };
  (portfolio?.services.bess ?? []).forEach(add);
  (portfolio?.services.eboiler ?? []).forEach(add);
  (portfolio?.services.pvCurtailment ?? []).forEach(add);
  return out;
};

/**
 * Why a point cannot be handed over as a group.
 * - "siblingNotInPortfolio": the flex site's other leg resolves to a connection
 *   point that is not among company A's switchable points, so it can never be
 *   put in the group — and the backend's `assertNoForeignSiblingEan` will refuse
 *   the switch with 409 CUSTOMER_SWITCH_FLEX_SIBLING_EAN. Caught HERE so the
 *   user is stopped at selection instead of at commit.
 * - "groupTooLarge": more than one additional member. The wire format carries at
 *   most one, so the row is BLOCKED — never silently truncated, because a
 *   truncated group produces exactly the 409 above.
 */
export type GroupBlockReason = "siblingNotInPortfolio" | "groupTooLarge";

export interface SwitchGroup {
  primary: CommodityPoint;
  /** The single ADDITIONAL member, or null when the group is one point. */
  member: CommodityPoint | null;
  /** The flex contracts that move with the group. */
  flex: FlexAsset[];
  /** Non-null ⇒ the row is rendered locked with this reason and cannot be sent. */
  blocked: GroupBlockReason | null;
}

/**
 * THE grouping rule. TWO WALKS, and both are required:
 *
 *  1. DIRECT HOP — identifies the sibling. Both `injection[]` and `offtake[]`
 *     rows carry `site_id` (they are joined to `sites` through their own leg), so
 *     the sibling is simply the other commodity point with the same `site_id`.
 *
 *  2. SITE-LEG CHECK — proves the group is COMPLETE. Commodity rows do NOT carry
 *     the site's leg ids; flex rows DO (the flex query selects `s.*`). The
 *     backend's guard resolves BOTH of the site's legs to an EAN and refuses
 *     unless each is part of the switch — and it resolves them from `sdps_*`
 *     regardless of owner or status. So a leg pointing at something outside A's
 *     switchable portfolio is invisible to walk 1 (no sibling appears) but is
 *     fatal at commit. Walk 2 is what turns that into a blocked row.
 *
 * SCOPING: grouping is only REQUIRED when flex is present. The backend guard
 * iterates the matched flex list and cannot fire when it is empty, so two EANs
 * sharing a FLEX-FREE site are not constrained and must never be force-grouped.
 *
 * All flex rows on one site share the same `sites` row, so the leg pair is a
 * property of the SITE, read once rather than per flex row.
 */
export const resolveSwitchGroup = (args: {
  primaryKey: string;
  points: CommodityPoint[];
  flex: FlexAsset[];
}): SwitchGroup | null => {
  const primary = args.points.find((p) => p.key === args.primaryKey);
  if (!primary) return null;

  const flexOnSite =
    primary.siteId == null
      ? []
      : args.flex.filter((f) => f.siteId === primary.siteId);

  // No transferable flex on the site ⇒ nothing links this point to any other,
  // and the backend imposes no sibling constraint. One point, no member.
  if (flexOnSite.length === 0) {
    return { primary, member: null, flex: [], blocked: null };
  }

  // Walk 2 — the SITE's legs, as the backend reads them.
  const legs: Array<{ direction: SwitchDirection; installationId: string }> = [];
  const seenLeg = new Set<string>();
  for (const f of flexOnSite) {
    const pairs: Array<[SwitchDirection, string | null]> = [
      ["injection", f.injectionInstallationId],
      ["offtake", f.offtakeInstallationId],
    ];
    for (const [direction, installationId] of pairs) {
      if (!installationId) continue;
      const legKey = `${direction}:${installationId}`;
      if (seenLeg.has(legKey)) continue;
      seenLeg.add(legKey);
      legs.push({ direction, installationId });
    }
  }

  const members = new Map<string, CommodityPoint>();
  for (const leg of legs) {
    const owner = args.points.find(
      (p) => p.installationIds[leg.direction] === leg.installationId
    );
    if (!owner) {
      // The backend WILL resolve this leg to an EAN and refuse. Stop here.
      return {
        primary,
        member: null,
        flex: flexOnSite,
        blocked: "siblingNotInPortfolio",
      };
    }
    if (owner.key !== primary.key) members.set(owner.key, owner);
  }

  if (members.size > 1) {
    return { primary, member: null, flex: flexOnSite, blocked: "groupTooLarge" };
  }

  return {
    primary,
    member: [...members.values()][0] ?? null,
    flex: flexOnSite,
    blocked: null,
  };
};

/** Every EAN in the group, primary first. Stored (prefixed) form, deduped. */
export const groupEans = (group: SwitchGroup): string[] =>
  group.member ? [group.primary.key, group.member.key] : [group.primary.key];

/**
 * A service-config string the backend can persist. `updateAtriasConfig` splits it
 * on "," into `service_component` / `service_config` and validates NOTHING, so an
 * empty or comma-less string is written verbatim as the wrong ATRIAS config.
 * A group member whose held direction has no well-formed config is therefore
 * refused at SELECTION rather than sent and bounced.
 */
export const isServiceConfigWellFormed = (config: string | null | undefined): boolean =>
  typeof config === "string" && config.trim() !== "" && config.includes(",");

/**
 * The onboarding leg(s) to run for ONE connection point.
 *
 * MOVED (not copied in spirit) out of `AddService.tsx::resolveCustomerSwitchLegs`,
 * with the excluded company id turned into an explicit parameter.
 *
 * 🔴 `destinationCompanyId` is company B — the RECEIVING company — NOT the company
 * the user is standing in. In the wizard those were the same value, because the
 * admin stood in B. In the Manage tab the user stands in A, the company that
 * HOLDS the rows, so passing A here makes `hasInj`/`hasOff` false, yields zero
 * legs, and blocks a point for a reason that is not true. The parameter is named
 * so the mistake is not expressible, and this function reads no store.
 *
 * When A holds BOTH directions we ALWAYS emit a SINGLE `combined` leg — never
 * two — regardless of SC_OFFINJE: one combined call does the complete
 * both-direction handover in one backend transaction, and splitting it is the
 * FT-3581-class trap described on `buildCommodityPoints`.
 */
export const resolveSwitchLegs = (args: {
  /**
   * `"SC_CODE,CONFIG"` entries. 🔴 `assessPoint` passes the EFFECTIVE list
   * (`resolveEffectiveAtriasConfig`), not the raw `sdpAtriasConfig` the name suggests —
   * the raw one is the backend's config-filtered subset. Same shape, wider source.
   */
  sdpAtriasConfig: string[] | undefined;
  checkExistingEan: ExistingCompany[] | undefined;
  destinationCompanyId: number;
}): CustomerSwitchLeg[] => {
  const cfgs = args.sdpAtriasConfig ?? [];
  // Same lookup as the combined-only gate in `assessPoint` and as `decideAskPhase`'s
  // existence rule — ONE definition of "the standalone config for this direction", so the
  // gate can never disagree with the value it is guarding. Behaviour-identical to the
  // inline `cfgs.find(...) || ""` this replaced.
  const injCfg = findDirectionServiceConfig(cfgs, "injection");
  const offCfg = findDirectionServiceConfig(cfgs, "offtake");
  const offinjeCfg = cfgs.find((c) => c.includes("SC_OFFINJE")) || "";

  const list = args.checkExistingEan ?? [];
  const cid = args.destinationCompanyId;
  const isActive = (c: ExistingCompany): boolean =>
    SWITCHABLE_STATUSES.includes((c.activeContract ?? "").trim().toLowerCase());
  const hasInj = list.some(
    (c) => c.direction.toLowerCase() === "injectie" && c.company_id !== cid && isActive(c)
  );
  const hasOff = list.some(
    (c) => c.direction.toLowerCase() === "afname" && c.company_id !== cid && isActive(c)
  );

  // More than one OTHER company holds this point: we cannot know whose contract
  // to end. Shared predicate with validateExistingEanContracts — these two once
  // disagreed, which is how an enabled action could run with zero legs and write
  // an orphan SDP under a false success.
  if (countForeignActiveOwners(list, cid) > 1) return [];

  if (hasInj && hasOff) {
    return [
      {
        direction: "combined",
        injectionServiceConfig: offinjeCfg || injCfg,
        offtakeServiceConfig: offinjeCfg || offCfg,
      },
    ];
  }
  if (hasInj) return [{ direction: "injection", injectionServiceConfig: injCfg }];
  if (hasOff) return [{ direction: "offtake", offtakeServiceConfig: offCfg }];
  return [];
};

/**
 * Why a point cannot be part of a transfer, decided from its PRESWITCH response.
 *
 * These cannot be answered from the portfolio: `checkExistingEan`, `sdpAtriasConfig`,
 * `typeOfMeter`, the running-process fields and `addPsl.status` all arrive with the
 * preswitch. So a ticked point can legitimately flip to blocked once its lookup lands —
 * that is honest, and the alternative (pre-fetching preswitch for every point in the
 * portfolio) means a real Atrias round trip for points nobody selected.
 *
 * TWO PHASES, and the split is about WHEN a reason can be answered:
 *   - `assessPointPreswitch` — `preswitchFailed`, `unsupportedMeter`,
 *     `capabilityUnknown`, `runningProcess`. Properties of the POINT; answerable the
 *     moment its lookup lands, with no destination.
 *   - `assessPoint` — the four above PLUS `multipleOwners`, `notForeign`,
 *     `combinedOnlyPartial`, `serviceConfigMissing`, `memberConfigMissing`, each of
 *     which mentions company B and so cannot be answered before one is chosen.
 *
 * Mirrors the wizard's own `canContinue()` gates, relocated to point scope.
 */
export type PointBlockReason =
  | "preswitchFailed"
  | "unsupportedMeter"
  /**
   * We hold NO EVIDENCE of what this EAN supports — see `isPointCapabilityUnknown`.
   *
   * 🔴 REPLACES `unsupportedConfig`, which conflated this with "the config codes are not
   * on our allow-list". Config codes no longer decide WHETHER a point may transfer, only
   * WHICH DIRECTIONS exist, so the second half of that reason is gone and the first half
   * is stated explicitly. Mirrors the backend's
   * `CUSTOMER_SWITCH_SERVICE_COMPONENTS_UNKNOWN`.
   */
  | "capabilityUnknown"
  | "runningProcess"
  | "multipleOwners"
  | "notForeign"
  | "combinedOnlyPartial"
  /**
   * A leg would go out carrying an EMPTY service config for a direction it is
   * transferring. Distinct from `combinedOnlyPartial` on purpose: that one says "this
   * direction only exists as part of a combined product", which is TRUE only when the
   * point actually carries SC_OFFINJE. Reusing it for the other shapes told the admin
   * the opposite of the problem — most absurdly on a blocked COMBINED leg, where the
   * copy would claim the point "only supports a combined contract".
   */
  | "serviceConfigMissing"
  | "memberConfigMissing";

export interface PointAssessment {
  blocked: PointBlockReason | null;
  legs: CustomerSwitchLeg[];
}

const UNSUPPORTED_METER_TYPES = ["MMR", "YMR"];

/**
 * WALLONIA, BY DGO NAME — and 🔴 THIS IS A HARDCODED STAND-IN.
 *
 * The real region key is `dgoTariffs.dgo_group` (the 8 names below with group
 * 'Ores', plus the independents that have no group). `dgo_group` is NOT exposed on
 * any payload this frontend receives — `/allservices` ships `si.dgo` / `so.dgo`,
 * the operator NAME, and nothing else — so the region test has to be made from the
 * name. If this proves brittle, the fix is to expose `dgo_group` on the portfolio
 * payload and delete this list; do not grow it indefinitely.
 *
 * WHY THE REGION MATTERS AT ALL. The meter gate below tests the LIVE PreswitchLight
 * `typeOfMeter`, and that value is produced by `getMeterType`
 * (yuso-portals-backend `functions/atriasUtils/preswitchLight.ts`), which only
 * recognises serials starting `1SAG` (Siemens) or `1LGZ` (Landis+Gyr) — the two
 * FLUVIUS suppliers. 84% of Walloon serials match neither and fall to an else
 * branch that reads MMR/YMR out of a single config character. Measured on prod:
 * 35.3% of Walloon points come back with an out-of-enum meter type versus 4.6% in
 * Flanders and 0% in Brussels. So in Wallonia — and only in Wallonia — the live
 * value is not evidence, and the stored `sdps_*.meterType` is used instead.
 *
 * The 8 `dgo_group = 'Ores'` operators, plus the four Walloon independents, plus
 * the bare "Ores" the column also holds on its own.
 */
export const WALLOON_DGOS: readonly string[] = [
  "Ores",
  "Brabant wallon",
  "Est",
  "Hainaut Électricité",
  "Hainaut Électricité2",
  "Luxembourg",
  "Mouscron",
  "Namur",
  "Verviers",
  "RESA",
  "AIEG",
  "AIESH",
  "Regie de Wavre",
];

const stripDiacritics = (value: string): string =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * 🔴 WORD-BOUNDARY, NOT SUBSTRING. The stored name is sometimes the operator alone
 * ("Ores", "RESA") and sometimes qualified ("ORES Namur"), so an equality test
 * misses half of them — but a bare substring test is worse: "Est" is a substring of
 * "Fluvius WEST-Vlaanderen", which would mark a FLEMISH point Walloon and hand it
 * the stored-meter-type exemption. `\b` refuses that (the "W" before "est" is a
 * word character) while still matching "ORES Est".
 *
 * Diacritics are stripped on BOTH sides so "Hainaut Électricité" and
 * "Hainaut Electricite" are the same operator. Mirrors the case-insensitive
 * name test the backend already uses for the Fluvius family
 * (`dgoTariffs.isFluviusDgo`).
 */
const WALLOON_DGO_PATTERNS: readonly RegExp[] = WALLOON_DGOS.map(
  (name) => new RegExp(`\\b${escapeRegExp(stripDiacritics(name))}\\b`, "i")
);

export const isWalloonDgo = (dgo: string | null | undefined): boolean => {
  const normalized = stripDiacritics((dgo ?? "").trim());
  if (normalized === "") return false;
  return WALLOON_DGO_PATTERNS.some((pattern) => pattern.test(normalized));
};

/** The stored fields the meter-type rules read. A `CommodityPoint` satisfies it. */
export type StoredMeterSource = Pick<CommodityPoint, "meterTypes" | "dgo">;

const isMeterTypeSupported = (value: string | null | undefined): boolean =>
  !UNSUPPORTED_METER_TYPES.includes((value ?? "").trim().toUpperCase());

/**
 * THE METER GATE. Flanders and Brussels keep the live PreswitchLight value, where
 * the classifier is reliable and an MMR/YMR is genuine. Wallonia uses the STORED
 * `sdps_*.meterType` instead, for the reason documented on `WALLOON_DGOS`.
 *
 * FUSED POINTS — acceptable if ANY direction's stored type is acceptable. The two
 * rows were written at different times by different classifiers
 * (`yuso-mig6-ws parsers/Other/FullPreswitchInformation.js` maps any non-smart
 * serial to AMR), so a disagreement is two classifiers disagreeing, not two meters.
 * Mirrors the site-linkage precedent above: either source row may be the one
 * carrying the usable fact.
 *
 * 🔴 A BLANK STORED VALUE DOES NOT BLOCK. 7.91% of Walloon portfolio rows carry no
 * meterType at all, and `getMeterType` returns AMR precisely when it saw no serial —
 * so blank means "we have no meter on file", never "the meter is unsupported".
 * Treating it as blocking would re-impose the very bug this rule removes on the
 * cohort with the least evidence against it.
 */
export const isMeterTypeAcceptable = (
  point: StoredMeterSource | null | undefined,
  liveTypeOfMeter: string | null | undefined
): boolean => {
  if (!isWalloonDgo(point?.dgo)) return isMeterTypeSupported(liveTypeOfMeter);
  const stored = Object.values(point?.meterTypes ?? {});
  return stored.length === 0 || stored.some(isMeterTypeSupported);
};

/** Deterministic order for picking the ONE value the wire can carry. */
const POSTED_METER_TYPE_ORDER: readonly SwitchDirection[] = ["injection", "offtake"];

/**
 * The `typeOfMeter` that must travel to company B.
 *
 * WITHOUT THIS THE GATE IS SELF-DEFEATING: `POST /sdp/addSdps/{B}` is the preswitch
 * response posted VERBATIM, and the backend writes `dataPSL.ean.typeOfMeter` straight
 * into `sdps_injection.meterType` / `sdps_offtake.meterType` (`functions/sdps.ts`).
 * We would allow the transfer and then persist the very MMR that used to block it,
 * so the point would be blocked again from company B.
 *
 * 🔴 ONE SLOT ON THE WIRE. That single field feeds BOTH INSERTs, so a per-direction
 * value is not expressible without a backend change (out of scope). The first
 * SUPPORTED stored value in a fixed direction order wins.
 *
 * NEVER overwrites with a blank, and never with an unsupported value: outside
 * Wallonia, and whenever there is nothing better to say, the live value goes out
 * unchanged — byte-identical to what ships today.
 *
 * ⚠️ KNOWN SIDE EFFECT, accepted. The same body feeds `addPslData`, which logs
 * `mig6_messages_preswitchligt.meterType` — a record of what ATRIAS said. Overriding
 * pollutes that log. Accepted because its enum is ('DMR','AMR','Legacy') and already
 * coerces MMR to '', so the log cannot record the truth either way.
 */
export const resolvePostedMeterType = (
  point: StoredMeterSource | null | undefined,
  liveTypeOfMeter: string | null | undefined
): string | null | undefined => {
  if (!isWalloonDgo(point?.dgo)) return liveTypeOfMeter;
  for (const direction of POSTED_METER_TYPE_ORDER) {
    const stored = (point?.meterTypes?.[direction] ?? "").trim();
    if (stored !== "" && isMeterTypeSupported(stored)) return stored;
  }
  return liveTypeOfMeter;
};

/**
 * The `POST /sdp/addSdps/{B}` body for one point: the preswitch response VERBATIM,
 * except that a Walloon point's `dataPSL.ean.typeOfMeter` carries the stored value.
 *
 * Returns the ORIGINAL OBJECT unchanged whenever nothing is overridden, so the
 * non-Walloon path is provably identical to today — including its identity, which
 * matters because the caller latches writes per EAN.
 */
export const buildAddSdpsBody = (
  psl: unknown,
  point: StoredMeterSource | null | undefined
): unknown => {
  const data = psl as
    | { dataPSL?: { ean?: { typeOfMeter?: string } } }
    | null
    | undefined;
  const ean = data?.dataPSL?.ean;
  if (!data || !data.dataPSL || !ean) return psl;
  const live = ean.typeOfMeter;
  const resolved = resolvePostedMeterType(point, live);
  if (resolved === live) return psl;
  return {
    ...data,
    dataPSL: { ...data.dataPSL, ean: { ...ean, typeOfMeter: resolved } },
  };
};

/**
 * Atrias processes that another party owns and that block onboarding this point.
 * Mirrors `validateRunningProcess` (components/utils/validation.ts).
 */
const BLOCKING_PROCESSES = ["Move-in", "Move in", "Start Access", "Start-Access"];

/**
 * The CAPABILITY fields of the preswitch `ean` block — what this connection point can
 * be contracted as. Named separately because the gates below read the UNFILTERED pair,
 * not `sdpAtriasConfig` alone; see `resolveEffectiveAtriasConfig`.
 */
export interface PointCapabilitySource {
  /** The backend's ALREADY CONFIG-FILTERED prio pick, one entry per surviving component. */
  sdpAtriasConfig?: string[];
  /** UNFILTERED, comma-joined SC_* codes. */
  allAvailableServiceComp?: string;
  /** UNFILTERED, ";"-separated per-component groups of comma-joined config codes. */
  allAvailableServiceCompConfig?: string;
}

/** The preswitch response shape both assessment phases read. */
export interface PointPreswitch {
  checkExistingEan?: ExistingCompany[];
  addPsl?: { status?: string };
  dataPSL?: {
    ean?: PointCapabilitySource & {
      typeOfMeter?: string;
      runningProcessModule?: string | null;
      runningProcessRequestorInvolvedBEAssociatedParty?: string | null;
    };
  };
}

const parseServiceComponentCodes = (value: string | undefined): string[] =>
  (value ?? "")
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code.startsWith("SC_"));

const splitConfigGroup = (group: string | undefined): string[] =>
  (group ?? "")
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code !== "");

/**
 * The point's service components in `"SC_CODE,CONFIG"` form — CONFIG CODES NO LONGER
 * DECIDE WHETHER A POINT MAY TRANSFER, only WHICH DIRECTIONS EXIST.
 *
 * 🔴 WHY THIS EXISTS AT ALL. `sdpAtriasConfig` reaches this module ALREADY
 * CONFIG-FILTERED: `preswitchLight.ts` (yuso-portals-backend) keeps each component's
 * prio config ONLY when its code is in that file's own `config` / `configAll` allow-list.
 * So a point whose components carry perfectly real but unlisted config codes arrives as
 * `sdpAtriasConfig: []` — byte-indistinguishable from a point we know NOTHING about.
 * Dropping the frontend gate alone would therefore have changed nothing; the derivation
 * has to move to a source the filter never touched.
 *
 * That source is already on the wire: `allAvailableServiceComp` /
 * `allAvailableServiceCompConfig`. Both are built from ONE `Object.keys()` iteration in
 * preswitchLight.ts, so they are index-aligned BY CONSTRUCTION. The same index-aligned
 * parse is performed by `AddService.tsx::parseServiceComponentData` and by the backend's
 * `getUsableServiceComponents` (`functions/customerSwitch.ts`) — three readers, ONE shape.
 * 🔴 The backend copy is the one that must stay in step with this function: it decides
 * the same question server-side and refuses with
 * `CUSTOMER_SWITCH_SERVICE_COMPONENTS_UNKNOWN`.
 *
 * 🔴 NOTHING THAT TRANSFERS TODAY CHANGES BYTE-FOR-BYTE. A component whose prio config
 * survived the backend allow-list keeps its ORIGINAL `sdpAtriasConfig` string VERBATIM,
 * because that exact string travels on as the leg's `serviceConfig` and is written by
 * `updateAtriasConfig` into `sdps_*.service_component` / `service_config`. A config code
 * is chosen here ONLY for components the filter had erased entirely — and the wire does
 * not carry the unfiltered prio pick, so the FIRST code of the component's own group is
 * taken (Atrias' own order).
 *
 * 🔴 A COMPONENT WITH NO CONFIG AT ALL IS DROPPED, never emitted bare.
 * `findDirectionServiceConfig` matches on `includes("SC_…")`, so a bare `"SC_INJECTI"`
 * would pass the non-empty `serviceConfigMissing` test and then be written verbatim as a
 * comma-less ATRIAS config by `updateAtriasConfig`, which splits on "," and validates
 * nothing. Dropping it keeps that refusal reachable — the hard block is untouched.
 *
 * Falls back to `sdpAtriasConfig` UNCHANGED when the unfiltered pair is absent (an older
 * payload, a fixture): strictly today's behaviour, never looser.
 */
export const resolveEffectiveAtriasConfig = (
  ean: PointCapabilitySource | null | undefined
): string[] => {
  const filtered = (ean?.sdpAtriasConfig ?? []).filter(
    (entry): entry is string => typeof entry === "string" && entry.trim() !== ""
  );
  const components = parseServiceComponentCodes(ean?.allAvailableServiceComp);
  if (components.length === 0) return filtered;

  const groups = (ean?.allAvailableServiceCompConfig ?? "").split(";");
  const effective: string[] = [];
  components.forEach((component, index) => {
    const surviving = filtered.find((entry) => entry.split(",")[0].trim() === component);
    if (surviving) {
      effective.push(surviving);
      return;
    }
    const [config] = splitConfigGroup(groups[index]);
    if (config === undefined) return;
    effective.push(`${component},${config}`);
  });
  // DEFENSIVE, and it is a one-way defence: a surviving entry whose component is absent
  // from the unfiltered list (shape drift between the two fields) must never be LOST.
  // Losing it would be a TIGHTENING, which is the one direction this change must not go.
  for (const entry of filtered) {
    if (!effective.includes(entry)) effective.push(entry);
  }
  return effective;
};

/**
 * THE EXPLICIT REFUSAL THAT REPLACES THE IMPLICIT ONE.
 *
 * The config filter used to refuse two very different points with one sentence: "we hold
 * no evidence of what this EAN supports" and "this EAN's config codes are not on a list".
 * Only the FIRST is a real reason to refuse a transfer, so it is now stated on its own —
 * an admin who sees it knows to re-run the lookup, and a point with odd config codes is
 * no longer swept up with it.
 *
 * "No evidence" means exactly: no preswitch payload, or `allAvailableServiceComp`
 * absent/empty, or parsing it yields zero `SC_*` components. A point that HAS components
 * is never unknown here, whatever its config codes say — that is the loosening. If those
 * components then yield no contractable config for a direction being transferred, the
 * UNCHANGED `serviceConfigMissing` / `combinedOnlyPartial` refusals still catch it.
 */
export const isPointCapabilityUnknown = (
  ean: PointCapabilitySource | null | undefined
): boolean => {
  if (parseServiceComponentCodes(ean?.allAvailableServiceComp).length > 0) return false;
  return resolveEffectiveAtriasConfig(ean).length === 0;
};

/**
 * PHASE 1 — the gates decidable from the PRESWITCH ALONE.
 *
 * `preswitchFailed`, `unsupportedMeter`, `capabilityUnknown` and `runningProcess`
 * are all properties of the CONNECTION POINT. None of them mentions company B, so
 * none of them needs a destination, and holding them back until one is chosen only
 * meant the user discovered a dead point later than we knew it was dead.
 *
 * Phase 2 (`assessPoint`) re-runs this first, so the two can never disagree —
 * there is ONE definition of each reason, and the split is about WHEN it can be
 * answered, not about what the answer is.
 */
export const assessPointPreswitch = (args: {
  /**
   * 🔴 NULLABLE ON PURPOSE. `useGroupPreswitch` stores `res.data.data`, so a 200
   * whose body has no `data` (an empty proxy response, a shape change) lands as a
   * `ready` entry holding UNDEFINED. This used to be unreachable — nothing read the
   * entry until a destination existed and the page had usually re-rendered past it —
   * but phase 1 now runs the instant the lookup lands, and an unguarded read here is
   * a white screen on the whole Manage page. Absent input is assessed, not trusted:
   * it carries no service components at all, so it FAILS CLOSED on `capabilityUnknown`.
   */
  psl: PointPreswitch | null | undefined;
  /**
   * The portfolio row(s) behind this EAN, for the Walloon meter-type exemption.
   * Absent/null ⇒ the live PreswitchLight value decides, i.e. the old behaviour.
   */
  point?: StoredMeterSource | null;
}): PointBlockReason | null => {
  const psl = args.psl ?? {};
  const ean = psl.dataPSL?.ean;

  if ((psl.addPsl?.status ?? "").toLowerCase() === "fail") {
    return "preswitchFailed";
  }
  if (!isMeterTypeAcceptable(args.point, ean?.typeOfMeter)) {
    return "unsupportedMeter";
  }
  /**
   * 🔴 ORDER IS LOAD-BEARING, and this is the half of it that lives in phase 1.
   *
   * This refusal is a property of the POINT, so it is answered here — which also puts it
   * ahead of every phase-2 reason, `notForeign` included. `assessPoint` resolves zero
   * legs for a point we know nothing about and would otherwise tell the admin it "is not
   * held in a way that allows a Customer Switch", which is not the problem and points at
   * the wrong remedy. Pinned by tests; do not move this below the leg resolution.
   */
  if (isPointCapabilityUnknown(ean)) {
    return "capabilityUnknown";
  }
  const runningProcess = ean?.runningProcessModule ?? null;
  if (
    runningProcess &&
    BLOCKING_PROCESSES.some((p) => runningProcess.toLowerCase().includes(p.toLowerCase())) &&
    ean?.runningProcessRequestorInvolvedBEAssociatedParty !== "Yuso"
  ) {
    return "runningProcess";
  }
  return null;
};

/**
 * PHASE 1 + PHASE 2. Everything below the phase-1 call needs company B, so this is
 * the form the page can only run once a destination exists.
 */
export const assessPoint = (args: {
  /** Nullable for the same reason as `assessPointPreswitch`'s. */
  psl: PointPreswitch | null | undefined;
  destinationCompanyId: number;
  /**
   * True for a point that is only in the group because a flex site links it. Its
   * service config must be well-formed for every direction it holds, because it is
   * sent as a group member and `updateAtriasConfig` splits that string on "," and
   * validates nothing — an empty one is written verbatim as the wrong ATRIAS config.
   * Refused HERE rather than sent and bounced with a 400.
   */
  isGroupMember: boolean;
  /** See `assessPointPreswitch`. */
  point?: StoredMeterSource | null;
}): PointAssessment => {
  const ean = args.psl?.dataPSL?.ean;
  const rows = args.psl?.checkExistingEan ?? [];

  const preswitchBlock = assessPointPreswitch({ psl: args.psl, point: args.point });
  if (preswitchBlock) {
    return { blocked: preswitchBlock, legs: [] };
  }
  /**
   * THE UNFILTERED capability view — `sdpAtriasConfig` alone is the backend's
   * allow-listed subset and would re-impose the very gate this change removes. Feeds
   * BOTH the leg resolution and the combined-only test below, so the service configs a
   * leg carries and the gate that judges them still come from ONE list.
   */
  const cfgs = resolveEffectiveAtriasConfig(ean);
  if (countForeignActiveOwners(rows, args.destinationCompanyId) > 1) {
    return { blocked: "multipleOwners", legs: [] };
  }

  const legs = resolveSwitchLegs({
    sdpAtriasConfig: cfgs,
    checkExistingEan: rows,
    destinationCompanyId: args.destinationCompanyId,
  });
  if (legs.length === 0) {
    return { blocked: "notForeign", legs: [] };
  }

  const leg = legs[0];

  /**
   * 🔴 THE CONFIGS THIS LEG WOULD ACTUALLY SEND, per direction — the ONE list both
   * gates below are asked of.
   *
   * The old gate asked "does this point carry a standalone SC_ tag ANYWHERE", which
   * mixes the two directions, and the mix is reachable: on
   * `["SC_OFFINJE,…","SC_OFFTAKE,…"]` with A holding INJECTION only, the leg is
   * `{direction:"injection", injectionServiceConfig:""}` while the OFFTAKE tag made the
   * mixed check pass. It also skipped `combined` legs entirely, which have the same hole
   * from the other side: on `["SC_OFFTAKE,…"]` with A holding BOTH directions the leg is
   * `{direction:"combined", injectionServiceConfig:""}` (the `offinjeCfg || injCfg`
   * fallback resolves to nothing). Either way an EMPTY string travels as a top-level
   * `injection`/`offtake` payload into contractOverview/addContract, where
   * `updateAtriasConfig` splits it on "," and validates nothing — a corrupt ATRIAS
   * config written silently, with no error anywhere.
   *
   * 🔴 READ THE LEG, do not re-run `findDirectionServiceConfig` here. These strings ARE
   * that helper's output (resolveSwitchLegs calls it), so there is still exactly ONE
   * direction→tag mapping — but only the leg expresses the COMBINED branch's
   * `offinjeCfg || injCfg` fallback. Re-running the helper for a combined leg would
   * over-block: it excludes SC_OFFINJE by design, so a legitimate SC_OFFINJE-only point
   * with A holding both directions — the ordinary combined-only handover — would be
   * refused.
   */
  const legConfigs: Array<string | undefined> =
    leg.direction === "combined"
      ? [leg.injectionServiceConfig, leg.offtakeServiceConfig]
      : leg.direction === "injection"
        ? [leg.injectionServiceConfig]
        : [leg.offtakeServiceConfig];

  if (legConfigs.some((c) => (c ?? "") === "")) {
    /**
     * WHICH refusal is this? `combinedOnlyPartial` (copy: "this connection point only
     * supports a combined offtake & injection contract, so a single direction cannot be
     * handed over on its own") is accurate for exactly ONE shape: a SINGLE-direction leg
     * on a point that carries SC_OFFINJE, where the direction genuinely exists only as
     * part of a combined product. That is the backend's own
     * CUSTOMER_SWITCH_COMBINED_ONLY_PARTIAL case, so it keeps its meaning.
     *
     * Everything else here is "the point has no contractable config for a direction we
     * are transferring" — a point with no SC_OFFINJE at all, or a COMBINED leg missing
     * one side, where the combined-only sentence would be actively misleading.
     */
    const isCombinedOnlyPoint = cfgs.some((c) => c.includes("SC_OFFINJE"));
    return {
      blocked:
        leg.direction !== "combined" && isCombinedOnlyPoint
          ? "combinedOnlyPartial"
          : "serviceConfigMissing",
      legs: [],
    };
  }

  if (args.isGroupMember) {
    // Strictly stricter than the empty check above: a group member's config must also be
    // SPLITTABLE, because `updateAtriasConfig` splits it on "," and validates nothing.
    if (!legConfigs.every(isServiceConfigWellFormed)) {
      return { blocked: "memberConfigMissing", legs: [] };
    }
  }

  return { blocked: null, legs };
};
